import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { Button } from '../components/design-system/primitives/Button/Button.js';
import { Card } from '../components/design-system/primitives/Card/Card.js';
import { Checkbox } from '../components/design-system/primitives/Checkbox/Checkbox.js';
import { Input } from '../components/design-system/primitives/Input/Input.js';
import { SEO } from '../components/seo/index.js';
import { getAuthFlowErrorMessage } from '../lib/authFlowErrors.js';
import { legalPathForLanguage } from '../lib/legalPaths.js';
import { consumePostAuthRedirect } from '../lib/postAuthRedirect.js';
import { useAuthStore } from '../store/authStore.js';

type StrengthKey = 'empty' | 'weak' | 'medium' | 'strong';

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const NAME_ALLOWED_REGEX = /^[\p{L}\p{M}][\p{L}\p{M}\s'.-]*$/u;

function strength(passwordValue: string): { key: StrengthKey; pct: number; className: string } {
  if (!passwordValue) return { key: 'empty', pct: 0, className: 'bg-ig-border' };
  if (passwordValue.length < 8) return { key: 'weak', pct: 33, className: 'bg-ig-danger' };
  const hasNumber = /\d/.test(passwordValue);
  const hasSpecial = /[^A-Za-z0-9]/.test(passwordValue);
  if (passwordValue.length >= 8 && hasNumber && hasSpecial) {
    return { key: 'strong', pct: 100, className: 'bg-ig-accent' };
  }
  return { key: 'medium', pct: 66, className: 'bg-ig-warning' };
}

function normalizeNameInput(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const registerWithEmail = useAuthStore((s) => s.registerWithEmail);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const termsPath = legalPathForLanguage(i18n.language, '/terms');
  const privacyPath = legalPathForLanguage(i18n.language, '/privacy');
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwStrength = useMemo(() => strength(password), [password]);

  useEffect(() => {
    if (isInitialized && user) {
      // Brand-new accounts (incl. the Google sign-up flow, which lands back
      // here after the redirect) go through the account-type wizard; anyone
      // whose account is older just gets the normal destination. Invite links
      // always win — consumePostAuthRedirect returns the stored path first.
      const accountAgeMs = user.createdAt ? Date.now() - new Date(user.createdAt).getTime() : Infinity;
      const isFreshAccount = accountAgeMs < 15 * 60 * 1000;
      navigate(consumePostAuthRedirect(isFreshAccount ? '/welcome' : '/dashboard'), { replace: true });
    }
  }, [isInitialized, user, navigate]);

  const onGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setError(getAuthFlowErrorMessage(err, t, 'register'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const normalizedName = normalizeNameInput(name);
    const normalizedEmail = email.trim();

    if (!normalizedName) {
      setError(t('auth.register.errors.nameRequired'));
      return;
    }
    if (normalizedName.length < NAME_MIN_LENGTH) {
      setError(t('auth.register.errors.nameTooShort', { min: NAME_MIN_LENGTH }));
      return;
    }
    if (normalizedName.length > NAME_MAX_LENGTH) {
      setError(t('auth.register.errors.nameTooLong', { max: NAME_MAX_LENGTH }));
      return;
    }
    if (!NAME_ALLOWED_REGEX.test(normalizedName)) {
      setError(t('auth.register.errors.nameInvalidChars'));
      return;
    }
    if (!emailValid) {
      setError(t('auth.register.errors.invalidEmail'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.register.errors.weakPassword'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.register.errors.passwordsNoMatch'));
      return;
    }
    if (!terms) {
      setError(t('auth.register.errors.acceptTerms'));
      return;
    }

    setLoading(true);
    try {
      await registerWithEmail(normalizedName, normalizedEmail, password);
      navigate(consumePostAuthRedirect('/welcome'), { replace: true });
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
      if (code === 'auth/email-already-in-use') {
        setError(t('auth.register.errors.emailAlreadyInUse'));
      } else {
        setError(t('auth.register.errors.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t('auth.createAccountTitle', { defaultValue: 'Create account' })}
        description={t('auth.register.metaDescription', { defaultValue: 'Create your InterviewGator account and start practicing realistic interview scenarios.' })}
      />
      <Card variant="outlined" className="w-full rounded-lg !border-0 bg-ig-bg-primary p-6 md:p-8">
        <div className="mb-6">
          <p className="mb-3 font-sans text-[11px] uppercase tracking-[0.136em] text-ig-accent">{t('auth.register.eyebrow')}</p>
          <h1 className="font-display text-[32px] font-normal leading-tight text-ig-text-primary">{t('auth.register.title')}</h1>
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <Input
            id="register-v2-name"
            label={t('auth.register.fullName')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            maxLength={NAME_MAX_LENGTH}
            placeholder={t('auth.register.fullNamePlaceholder')}
            className="h-12 rounded-md !border-0 bg-ig-bg-tertiary"
          />

          <Input
            id="register-v2-email"
            type="email"
            label={t('auth.register.email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder={t('auth.register.emailPlaceholder')}
            error={Boolean(email) && !emailValid}
            helperText={email && !emailValid ? t('auth.register.emailInvalid') : undefined}
            className="h-12 rounded-md !border-0 bg-ig-bg-tertiary"
          />

          <div className="pb-3">
            <Input
              id="register-v2-password"
              type={showPassword ? 'text' : 'password'}
              label={t('auth.register.password')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              placeholder={t('auth.register.passwordPlaceholder')}
              className="h-12 rounded-md !border-0 bg-ig-bg-tertiary pr-20"
            />
            <div className="-mt-9 flex justify-end pr-3">
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="text-[13px] text-ig-text-secondary hover:text-ig-text-primary"
              >
                {showPassword ? t('common.hide') : t('common.show')}
              </button>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-ig-bg-tertiary">
                <div className={`h-full transition-all ${pwStrength.className}`} style={{ width: `${pwStrength.pct}%` }} />
              </div>
              <span className="text-[11px] text-ig-text-tertiary">{t(`auth.register.strength.${pwStrength.key}`)}</span>
            </div>
          </div>

          <div className="pb-3">
            <Input
              id="register-v2-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              label={t('auth.register.confirmPassword')}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              className="h-12 rounded-md !border-0 bg-ig-bg-tertiary pr-20"
            />
            <div className="-mt-9 flex justify-end pr-3">
              <button
                type="button"
                onClick={() => setShowConfirmPassword((value) => !value)}
                className="text-[13px] text-ig-text-secondary hover:text-ig-text-primary"
              >
                {showConfirmPassword ? t('common.hide') : t('common.show')}
              </button>
            </div>
          </div>

          <Checkbox
            checked={terms}
            onCheckedChange={(value) => setTerms(value === true)}
            label={
              <>
                {t('auth.register.agreePrefix')}{' '}
                <Link to={termsPath} className="text-ig-accent hover:underline">
                  {t('auth.login.terms')}
                </Link>{' '}
                {t('auth.login.and')}{' '}
                <Link to={privacyPath} className="text-ig-accent hover:underline">
                  {t('auth.login.privacy')}
                </Link>{' '}
                {t('auth.register.ageConfirm')}
              </>
            }
            className="mt-3 text-[12px] text-ig-text-secondary"
          />

          <Button
            type="button"
            variant="secondary"
            size="lg"
            loading={loading}
            onClick={() => void onGoogle()}
            leftIcon={<Icon name="google" className="h-5 w-5" />}
            className="w-full rounded-md !border-0"
          >
            {t('auth.login.continueWithGoogle')}
          </Button>

          {error ? <p className="text-[13px] text-ig-danger">{error}</p> : null}

          <Button type="submit" variant="primary" size="lg" loading={loading} className="w-full rounded-md">
            {t('auth.register.submit')}
          </Button>
        </form>

        <div className="mt-6 space-y-3 text-center">
          <p className="text-[14px] text-ig-text-secondary">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="text-ig-accent hover:underline">
              {t('auth.register.signIn')}
            </Link>
          </p>
          <p className="whitespace-pre-line text-[12px] text-ig-text-tertiary">{t('auth.register.footer')}</p>
        </div>
      </Card>
    </>
  );
}
